import React from 'react'
import { motion } from 'framer-motion'
import { 
  Leaf, 
  FlaskConical, 
  Bird, 
  ShieldCheck, 
  Info,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import { Ingredient, DietaryClaims } from '../../services/mealmateService'
import { Badge } from '../ui/Badge'

interface IngredientTransparencyProps {
  ingredients: Ingredient[]
  claims: DietaryClaims
}

const IngredientTransparency: React.FC<IngredientTransparencyProps> = ({ ingredients, claims }) => {
  const getOriginIcon = (origin: string) => {
    switch (origin) {
      case 'natural': return <Leaf className="w-3 h-3 text-emerald-500" />
      case 'animal': return <Bird className="w-3 h-3 text-amber-500" />
      case 'synthetic': return <FlaskConical className="w-3 h-3 text-purple-500" />
      default: return <Info className="w-3 h-3 text-theme-text-muted" />
    }
  }

  const getOriginColor = (origin: string) => {
    switch (origin) {
      case 'natural': return 'bg-emerald-50 text-emerald-700 border-emerald-100'
      case 'animal': return 'bg-amber-50 text-amber-700 border-amber-100'
      case 'synthetic': return 'bg-purple-50 text-purple-700 border-purple-100'
      default: return 'bg-theme-surface-light text-theme-text-secondary'
    }
  }

  return (
    <div className="space-y-4 p-4 bg-theme-surface-light rounded-xl border border-theme-border">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-bold text-theme-text-primary flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-theme-primary" />
          Transparency Layer
        </h4>
        <Info className="w-4 h-4 text-theme-text-muted cursor-help" />
      </div>

      {/* Dietary Claims Summary */}
      <div className="flex flex-wrap gap-2 mb-4">
        {claims.isVegan && (
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            100% Vegan
          </Badge>
        )}
        {claims.isVegetarian && !claims.isVegan && (
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            100% Vegetarian
          </Badge>
        )}
        {claims.isNatural && (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            100% Natural
          </Badge>
        )}
        {claims.isSyntheticFree && (
          <Badge className="bg-purple-500/10 text-purple-600 border-purple-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Synthetic Free
          </Badge>
        )}
        {!claims.isVegan && !claims.isVegetarian && (
          <Badge className="bg-amber-500/10 text-amber-600 border-amber-200">
            <AlertCircle className="w-3 h-3 mr-1" />
            Contains Animal Products
          </Badge>
        )}
      </div>

      {/* Structured Ingredients List */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-theme-text-secondary mb-1">Ingredient Sources:</p>
        <div className="grid grid-cols-1 gap-2">
          {ingredients.map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`flex items-center justify-between p-2 rounded-lg border ${getOriginColor(item.origin)} bg-opacity-40`}
            >
              <div className="flex items-center gap-2">
                {getOriginIcon(item.origin)}
                <span className="text-xs font-medium">{item.name}</span>
                {item.amount && (
                  <span className="text-[10px] font-bold text-theme-primary opacity-60 ml-1">
                    ({item.amount}{item.unit})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                  {item.origin}
                </span>
                {item.isSynthetic && (
                  <Badge variant="outline" className="text-[8px] py-0 px-1 border-purple-200 text-purple-600">
                    Synthetic
                  </Badge>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <p className="text-[10px] text-theme-text-muted italic mt-2">
        * Transparency is our priority. Origin data is verified through our partner supply chain auditing system.
      </p>
    </div>
  )
}

export default IngredientTransparency
